import { z } from 'zod';
import { recurrenceSchema } from './campaigns';

export const ACTIVITY_SUBTYPES = [
  { value: 'task', label: 'Task' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
] as const;
export const ACTIVITY_STATUSES = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting', label: 'Waiting on someone else' },
  { value: 'completed', label: 'Completed' },
  { value: 'deferred', label: 'Deferred' },
] as const;
export const ACTIVITY_PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
] as const;

export type ActivityKind = 'task' | 'event';
export type WhoType = 'lead' | 'contact';
export type WhatType = 'account' | 'opportunity';

export interface Activity {
  id: string;
  version: number;
  ownerId: string;
  kind: ActivityKind;
  subtype: (typeof ACTIVITY_SUBTYPES)[number]['value'];
  subject: string;
  status: (typeof ACTIVITY_STATUSES)[number]['value'];
  priority: (typeof ACTIVITY_PRIORITIES)[number]['value'];
  dueDate: string | null;
  startAt: string | null;
  endAt: string | null;
  location: string | null;
  whoType: WhoType | null;
  whoId: string | null;
  whatType: WhatType | null;
  whatId: string | null;
  description: string | null;
  completedAt: string | null;
  /** Who wrote it (may edit it too, e.g. the salesperson who handed a deal to pre-sales). */
  createdBy?: string | null;
  /** Set for occurrences of a repeating task or event. */
  seriesId?: string | null;
  /** Contacts related through shared activities (the primary contact is whoId). */
  relatedContactIds?: string[];
  /** Events: other CRM users invited, with their answer. */
  guests?: { id: string; response: 'pending' | 'accepted' | 'declined' }[];
  createdAt: string;
  updatedAt: string;
}

const uuid = z.string().uuid();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const opt = <T extends z.ZodTypeAny>(s: T) => z.preprocess((v) => (v === '' ? null : v), s.nullable()).optional();

const base = {
  subject: z.string().trim().min(1, 'Add a subject').max(255),
  subtype: z.enum(['task', 'call', 'email', 'meeting']).optional(),
  priority: z.enum(['high', 'normal', 'low']).optional(),
  status: z.enum(['not_started', 'in_progress', 'waiting', 'completed', 'deferred']).optional(),
  description: opt(z.string().trim().max(32000)),
  location: opt(z.string().trim().max(255)),
  dueDate: opt(date),
  startAt: opt(z.string().datetime({ offset: true })),
  endAt: opt(z.string().datetime({ offset: true })),
  whoType: opt(z.enum(['lead', 'contact'])),
  whoId: opt(uuid),
  whatType: opt(z.enum(['account', 'opportunity'])),
  whatId: opt(uuid),
  ownerId: uuid.optional(),
  /** Shared activities: further contacts this activity relates to, besides the primary contact (whoId). */
  relatedContactIds: z.array(uuid).max(50).optional(),
  /** Events: other CRM users invited to the meeting (it appears in their calendar). */
  guestUserIds: z.array(uuid).max(30).optional(),
};

export const activityCreateSchema = z
  .object({ kind: z.enum(['task', 'event']), ...base, notifyUserIds: z.array(uuid).max(20).optional(), recurrence: recurrenceSchema.optional() })
  .strict()
  .superRefine((a, ctx) => {
    if (a.recurrence && (a.kind === 'task' ? !a.dueDate : !a.startAt)) ctx.addIssue({ code: 'custom', path: ['recurrence'], message: a.kind === 'task' ? 'Repeating tasks need a due date' : 'Repeating events need a start' });
    if (!!a.whoType !== !!a.whoId) ctx.addIssue({ code: 'custom', path: ['whoId'], message: 'Choose who this is about' });
    if (!!a.whatType !== !!a.whatId) ctx.addIssue({ code: 'custom', path: ['whatId'], message: 'Choose what this is related to' });
    if (a.kind === 'event') {
      if (!a.startAt || !a.endAt) ctx.addIssue({ code: 'custom', path: ['startAt'], message: 'Events need a start and end time' });
      else if (new Date(a.endAt) < new Date(a.startAt)) ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'End must be after start' });
    }
  });

export const activityUpdateSchema = z.object({ ...base, subject: base.subject.optional() }).strict();

export type ActivityCreateInput = z.infer<typeof activityCreateSchema>;
export type ActivityUpdateInput = z.infer<typeof activityUpdateSchema>;

export const activityQuerySchema = z.object({
  /** Timeline for one record. */
  objectName: z.enum(['account', 'contact', 'lead', 'opportunity']).optional(),
  recordId: uuid.optional(),
  /** "My work": open tasks due up to a date and events in a window. */
  mine: z.enum(['true', 'false']).optional(),
  open: z.enum(['true', 'false']).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  dueOnOrBefore: date.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
